import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function SubmissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSubmission();
  }, [id]);

  const fetchSubmission = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/submissions/${id}`,
        { withCredentials: true }
      );
      setSubmission(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/admin');
      } else if (err.response?.status === 404) {
        setError('Submission not found');
      } else {
        setError('Failed to load submission');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/submissions/${id}`,
        { status: newStatus },
        { withCredentials: true }
      );
      setSubmission(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      console.error('Status update failed');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;

    try {
      await axios.delete(
        `${API_URL}/api/admin/submissions/${id}`,
        { withCredentials: true }
      );
      navigate('/admin/dashboard');
    } catch (err) {
      console.error('Delete failed');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const parseJSON = (str) => {
    try {
      return JSON.parse(str);
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="admin-layout">
        <aside className="admin-sidebar">
          <div className="admin-logo">SNA Renewals</div>
        </aside>
        <main className="admin-main">
          <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-layout">
        <aside className="admin-sidebar">
          <div className="admin-logo">SNA Renewals</div>
        </aside>
        <main className="admin-main">
          <div className="alert alert-error">{error}</div>
          <Link to="/admin/dashboard" className="btn btn-secondary">← Back to Dashboard</Link>
        </main>
      </div>
    );
  }

  const equipmentBreakdown = parseJSON(submission.equipment_breakdown);
  const arubaLicenseTypes = parseJSON(submission.aruba_license_types);

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="admin-logo">SNA Renewals</div>
        <nav>
          <ul className="admin-nav">
            <li><Link to="/admin/dashboard">📊 Dashboard</Link></li>
            <li><Link to="/">📝 View Form</Link></li>
          </ul>
        </nav>
      </aside>

      <main className="admin-main">
        <div className="detail-header">
          <div>
            <Link to="/admin/dashboard" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.875rem' }}>
              ← Back to Dashboard
            </Link>
            <h1 style={{ marginTop: '0.5rem' }}>Submission #{submission.id}</h1>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <select
              className={`badge badge-${submission.status}`}
              value={submission.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              style={{ border: '1px solid #e2e8f0', cursor: 'pointer', padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.875rem' }}
            >
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </select>
            <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-icon">📍</div>
            <h2>Site Information</h2>
          </div>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Site Name</label>
              <div className="value">{submission.site_name}</div>
            </div>
            <div className="detail-item">
              <label>HTL Code / Site ID</label>
              <div className="value">{submission.htl_code || '—'}</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-icon">🔧</div>
            <h2>Equipment Details</h2>
          </div>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Vendor</label>
              <div className="value" style={{ textTransform: 'capitalize' }}>{submission.vendor}</div>
            </div>
            {submission.vendor !== 'meraki' && (
              <>
                <div className="detail-item">
                  <label>Model(s)</label>
                  <div className="value">{submission.model || '—'}</div>
                </div>
                <div className="detail-item">
                  <label>Serial Number(s)</label>
                  <div className="value" style={{ whiteSpace: 'pre-line' }}>{submission.serial_numbers || '—'}</div>
                </div>
              </>
            )}
          </div>

          {submission.vendor === 'meraki' && equipmentBreakdown && (
            <div style={{ marginTop: '1.5rem' }}>
              <label style={{ marginBottom: '0.75rem', display: 'block' }}>Equipment License Breakdown</label>
              <table className="equipment-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {equipmentBreakdown.map((item, index) => (
                    <tr key={index}>
                      <td>{item.model || '—'}</td>
                      <td>{item.qty || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {submission.vendor === 'aruba' && arubaLicenseTypes && (
            <div style={{ marginTop: '1.5rem' }}>
              <label style={{ marginBottom: '0.75rem', display: 'block' }}>Aruba License Types</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {arubaLicenseTypes.foundation_care && <span className="badge badge-in_progress">HPE Foundation Care</span>}
                {arubaLicenseTypes.pef_license && <span className="badge badge-in_progress">PEF License</span>}
                {arubaLicenseTypes.central_ap && <span className="badge badge-in_progress">Central AP License</span>}
                {!arubaLicenseTypes.foundation_care && !arubaLicenseTypes.pef_license && !arubaLicenseTypes.central_ap && (
                  <span style={{ color: '#6b7280' }}>No specific license types selected</span>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-icon">📋</div>
            <h2>Renewal Details</h2>
          </div>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Renewal Type</label>
              <div className="value">{submission.renewal_type}</div>
            </div>
            <div className="detail-item">
              <label>Quantity</label>
              <div className="value">{submission.quantity || '—'}</div>
            </div>
            <div className="detail-item">
              <label>AP Count</label>
              <div className="value">{submission.ap_count || '—'}</div>
            </div>
            <div className="detail-item">
              <label>Expiration Date</label>
              <div className="value" style={{ color: '#dc2626', fontWeight: '600' }}>
                {new Date(submission.expiration_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-icon">👤</div>
            <h2>Point of Contact</h2>
          </div>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Name</label>
              <div className="value">{submission.contact_name || '—'}</div>
            </div>
            <div className="detail-item">
              <label>Phone</label>
              <div className="value">
                {submission.contact_phone ? (
                  <a href={`tel:${submission.contact_phone}`} style={{ color: '#3b82f6' }}>{submission.contact_phone}</a>
                ) : '—'}
              </div>
            </div>
            <div className="detail-item">
              <label>Email</label>
              <div className="value">
                {submission.contact_email ? (
                  <a href={`mailto:${submission.contact_email}`} style={{ color: '#3b82f6' }}>{submission.contact_email}</a>
                ) : '—'}
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-icon">📎</div>
            <h2>Additional Information</h2>
          </div>
          
          {submission.screenshot_path && (
            <div className="detail-item" style={{ marginBottom: '1.5rem' }}>
              <label>Screenshot</label>
              <div style={{ marginTop: '0.5rem' }}>
                <a href={`${API_URL}/uploads/${submission.screenshot_path}`} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
                  📷 View Screenshot
                </a>
              </div>
              <div style={{ marginTop: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                <img 
                  src={`${API_URL}/uploads/${submission.screenshot_path}`}
                  alt="Submission screenshot"
                  style={{ maxWidth: '100%', display: 'block' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            </div>
          )}

          <div className="detail-item">
            <label>Notes</label>
            <div className="value" style={{ whiteSpace: 'pre-line' }}>{submission.notes || 'No additional notes'}</div>
          </div>
        </div>

        <div className="card" style={{ background: '#f8fafc' }}>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Submitted</label>
              <div className="value" style={{ fontSize: '0.875rem', color: '#6b7280' }}>{formatDate(submission.created_at)}</div>
            </div>
            <div className="detail-item">
              <label>Last Updated</label>
              <div className="value" style={{ fontSize: '0.875rem', color: '#6b7280' }}>{formatDate(submission.updated_at)}</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default SubmissionDetail;
